/**
 * MatDatepicker Japanese Internationalization
 *
 * Provides Japanese translations for datepicker navigation buttons,
 * aria labels, and other UI elements.
 */

import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { MatDatepickerIntl } from '@angular/material/datepicker';

@Injectable()
export class JapaneseDatepickerIntl extends MatDatepickerIntl {
  private readonly isEnglish: boolean;

  constructor(@Inject(LOCALE_ID) localeId: string) {
    super();
    this.isEnglish = (localeId || '').toLowerCase().startsWith('en');
    this.applyLabels();
  }

  private applyLabels(): void {
    if (this.isEnglish) {
      this.calendarLabel = 'Calendar';
      this.openCalendarLabel = 'Open calendar';
      this.closeCalendarLabel = 'Close calendar';
      this.prevMonthLabel = 'Previous month';
      this.nextMonthLabel = 'Next month';
      this.prevYearLabel = 'Previous year';
      this.nextYearLabel = 'Next year';
      this.prevMultiYearLabel = 'Previous 24 years';
      this.nextMultiYearLabel = 'Next 24 years';
      this.switchToMonthViewLabel = 'Choose month';
      this.switchToMultiYearViewLabel = 'Choose year';
      this.startDateLabel = 'Start date';
      this.endDateLabel = 'End date';
      return;
    }
    this.calendarLabel = 'カレンダー';
    this.openCalendarLabel = 'カレンダーを開く';
    this.closeCalendarLabel = 'カレンダーを閉じる';
    this.prevMonthLabel = '前月';
    this.nextMonthLabel = '翌月';
    this.prevYearLabel = '前年';
    this.nextYearLabel = '翌年';
    this.prevMultiYearLabel = '前の24年';
    this.nextMultiYearLabel = '次の24年';
    this.switchToMonthViewLabel = '月を選択';
    this.switchToMultiYearViewLabel = '年を選択';
    this.startDateLabel = '開始日';
    this.endDateLabel = '終了日';
  }

  /** A label for the calendar popup (used by screen readers). */
  override calendarLabel = 'カレンダー';

  /** A label for the button used to open the calendar popup (used by screen readers). */
  override openCalendarLabel = 'カレンダーを開く';

  /** Label for the button used to close the calendar popup. */
  override closeCalendarLabel = 'カレンダーを閉じる';

  /** A label for the previous month button (used by screen readers). */
  override prevMonthLabel = '前月';

  /** A label for the next month button (used by screen readers). */
  override nextMonthLabel = '翌月';

  /** A label for the previous year button (used by screen readers). */
  override prevYearLabel = '前年';

  /** A label for the next year button (used by screen readers). */
  override nextYearLabel = '翌年';

  /** A label for the previous multi-year button (used by screen readers). */
  override prevMultiYearLabel = '前の24年';

  /** A label for the next multi-year button (used by screen readers). */
  override nextMultiYearLabel = '次の24年';

  /** A label for the 'switch to month view' button (used by screen readers). */
  override switchToMonthViewLabel = '月を選択';

  /** A label for the 'switch to year view' button (used by screen readers). */
  override switchToMultiYearViewLabel = '年を選択';

  /** A label for the first date of a range of dates (used by screen readers). */
  override startDateLabel = '開始日';

  /** A label for the last date of a range of dates (used by screen readers). */
  override endDateLabel = '終了日';

  /** Formats a range of years (used for visuals). */
  override formatYearRange(start: string, end: string): string {
    if (this.isEnglish) {
      return `${start} - ${end}`;
    }
    return `${start}年 〜 ${end}年`;
  }

  /** Formats a label for a range of years (used by screen readers). */
  override formatYearRangeLabel(start: string, end: string): string {
    if (this.isEnglish) {
      return `${start} to ${end}`;
    }
    return `${start}年から${end}年`;
  }
}
